import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

export type HomeEntity = {
  id: string; name: string; province: string;
  region?: string | null; latitude?: number | null; longitude?: number | null;
} | null;

export type AuthUser = { id: string; username: string; displayName: string | null; role: string } | null;

type AuthState = {
  status: 'loading' | 'authed' | 'unauthed';
  role: string | null;          // 'admin' | 'member' | 'ca_user'
  homeEntity: HomeEntity;
  user: AuthUser;
};

// isMember: same access as an admin, minus the Admin, PDF Upload and Reports areas.
type AuthContextValue = AuthState & {
  refresh: () => Promise<void>; isAdmin: boolean; isCAUser: boolean; isMember: boolean;
};

const AuthContext = createContext<AuthContextValue>({
  status: 'loading', role: null, homeEntity: null, user: null,
  refresh: async () => {}, isAdmin: false, isCAUser: false, isMember: false,
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>({ status: 'loading', role: null, homeEntity: null, user: null });

  const refresh = useCallback(async () => {
    try {
      const r = await fetch('/api/is-authenticated', { credentials: 'include' });
      const d = await r.json();
      setState(d.authenticated
        ? { status: 'authed', role: d.role || 'admin', homeEntity: d.homeEntity || null, user: d.user || null }
        : { status: 'unauthed', role: null, homeEntity: null, user: null });
    } catch {
      setState({ status: 'unauthed', role: null, homeEntity: null, user: null });
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <AuthContext.Provider value={{
      ...state, refresh,
      isAdmin: state.role === 'admin',
      isCAUser: state.role === 'ca_user',
      isMember: state.role === 'member',
    }}>
      {children}
    </AuthContext.Provider>
  );
};
